//mirrors `var defaults = {...}` in intlTelInput.js v29.1.2 — keep in sync when upgrading the lib,
//so wrapper-only bookkeeping options (showError, required, countryPrefix, etc.) never reach the library
//and don't trigger its "Unknown option" console warnings
//
//NOTE: this file is bundled by r.js into user_public/pwa (via the google.intl.phone.init shim in
//their init.js) and optimized with r.js's own bundled UglifyJS, which only understands ES5 or
//earlier syntax (no let/const/class/arrow functions/shorthand methods/template literals) —
//verified: a stray `let` here made r.js's minifier silently skip minification for the whole
//bundle. Keep this file plain ES5 (var/function) even though the vendored intlTelInput.js itself
//is free to use modern syntax (it's never passed through this minifier).
var INTL_TEL_INPUT_SUPPORTED_OPTIONS = [
    "countrySelectorMode", "allowedNumberTypes", "allowNumberExtensions", "allowPhonewords",
    "containerClass", "countryNameLocale", "countryNameOverrides", "countryOrder", "countrySearch",
    "customPlaceholder", "dropdownAlwaysOpen", "dropdownParent", "excludeCountries", "matchDropdownWidth",
    "formatAsYouType", "hiddenInputs", "uiTranslations", "initialCountry", "initialCountryLookup",
    "loadUtils", "numberDisplayFormat", "onlyCountries", "placeholderNumberPolicy", "placeholderNumberType",
    "searchInputClass", "separateDialCode", "strictRejectAnimation", "showFlags", "strictMode"
];

var DEFAULT_ERROR_MAP = Object.freeze({
    0: "Invalid number",
    1: "Invalid country code",
    2: "Too short",
    3: "Too long",
    4: "Invalid number",
    '-99': "error_value_is_empty"
});

//not a real intl-tel-input option — read by this wrapper only (see initPhoneInput/getParam usage)
var DEFAULT_OPTIONS = Object.freeze({
    showError: true, //show error
    showErrorBorder: true, // show red border on error
    required: true,  // is field required
    initialCountry: null, //preselect country on init
    //numberDisplayFormat: 'NATIONAL', //replaces removed v18-era nationalMode:true option (v19+); leave unset for the old nationalMode:false equivalent
    startCheck: true,  //show error on init
    allowedPrefix: false,  //filter country list by prefix
    countryPrefix: null,
    countryPrefixId: null,
    triggerChangeHidden: false, //add keyup event as change to hidden input, works only with hiddenInput value
    //iti.isValidNumber() only checks whether the number is a POSSIBLE length for the region
    //("possible" mode internally) — a too-short number (e.g. a German number missing most of
    //its digits) can still pass it. iti.isValidNumberPrecise() runs the library's actual
    //(libphonenumber) validity check and would catch that, but is meaningfully stricter and
    //could newly reject numbers that validate fine today across many countries — this flag
    //lets a specific field opt into the stricter check without changing the default everywhere
    //until that's been reviewed. Default kept as `false` (old behavior, isValidNumber()).
    strictValidation: false,
    //loadUtilsOnInit: "/v2/lib/intl-tel-input/js/utils.js",
    loadUtils: window.loadUtilsFunction,
    separateDialCode: false, //v29 default changed to true, keep previous look

    errorTemplate: "<p class=\"input-error\">{{error}}</p>",
    onValid: null,
    onNotValid: null
    //allowedNumberTypes: null, //"FIXED_LINE_OR_MOBILE", (renamed from validationNumberTypes in v19+)
    //^ commented out on purpose (was forced to `null` = no type restriction, which also disables
    //the library's own MOBILE/FIXED_LINE length sanity check — this is why implausibly short
    //numbers, e.g. a German number missing most of its digits, were accepted as "valid": with no
    //type filter, isValidNumber()'s loose "possible" check accepts far more than a real mobile/
    //landline number could ever be. Leaving this option unset restores the library's own default
    //(["MOBILE", "FIXED_LINE"]) instead of hardcoding a copy of it here.
});

//accepts a CSS selector string, a DOM element, or a jQuery-like object (some callers still pass one)
var SIMPLE_ID_SELECTOR = /^#[^\s.,>+~:\[\]]+$/;
function toElement(target) {
    if (!target) {
        return null;
    }
    if (typeof target === 'string') {
        //jQuery/Sizzle resolves a plain "#id" selector via getElementById, which (unlike
        //querySelector) does no CSS parsing and accepts ANY string as an id — this codebase
        //has real DOM ids that aren't valid CSS identifiers (e.g. numeric provider ids like
        //"1"), and document.querySelector('#1') throws "SyntaxError: '#1' is not a valid
        //selector" since a CSS id selector can't start with a digit unless escaped
        if (SIMPLE_ID_SELECTOR.test(target)) {
            return document.getElementById(target.slice(1));
        }
        return document.querySelector(target);
    }
    if (target.jquery) {
        return target.length ? target[0] : null;
    }
    return target;
}

//jQuery('<p>...</p>') equivalent — turns an HTML string into a single detached element
function htmlToElement(html) {
    var template = document.createElement('template');
    template.innerHTML = String(html).trim();
    return template.content.firstElementChild;
}

//best-effort longest-dial-code-prefix match for a pasted string — used only to pre-correct
//the selected country ahead of the library's own strictMode paste handling (see the 'input'
//listener in initPhoneInput). Doesn't need area-code-level precision (may pick a different
//same-dial-code country than the library ultimately settles on, e.g. gb vs gg for +44) — it
//only needs to be in the right length ballpark.
//
//Requires a leading "+": a bare local-format paste (e.g. "2025551234", no "+") isn't an
//international number at all and must NOT trigger country re-detection — matching the
//library's own #resolveCountryChangeFromNumber, which likewise only treats a number as
//dial-code-bearing when it starts with "+". Without this guard, "2025551234" matches Egypt's
//dial code "20" as a prefix and wrongly flips the selected country to Egypt.
function findCountryByDialCodePrefix(text) {
    var match = String(text).match(/^\+(\d+)/);
    if (!match || !window.intlTelInput || typeof window.intlTelInput.getAllCountries !== 'function') {
        return null;
    }
    var digits = match[1];
    var countries = window.intlTelInput.getAllCountries();
    var best = null;
    for (var i = 0; i < countries.length; i++) {
        var country = countries[i];
        if (digits.indexOf(country.dialCode) === 0 && (!best || country.dialCode.length > best.dialCode.length)) {
            best = country;
        }
    }
    return best;
}

//given raw phone text and the iso2 of the country currently selected in the widget, returns
//the most specific country within that SAME dial-code family (e.g. gb/gg/je/im, all sharing
//"44") whose areaCodes match the digits following the shared dial code — or null if nothing
//more specific than the current selection is found. Exists because the library's own
//re-detection (and findCountryByDialCodePrefix above) only kicks in for text starting with
//"+" — a bare-digit number like "447911123456" never gets the same area-code-specific
//resolution a "+"-prefixed paste of the same digits does, even though the digits are
//identical. Deliberately scoped to the CURRENT country's own dial-code family only (never
//scans unrelated families) so it can't reintroduce the false-positive
//findCountryByDialCodePrefix's own "+" requirement guards against (e.g. a bare NANP number
//like "2025551234" getting reinterpreted via some unrelated country's dial code).
function findMostSpecificCountryInFamily(text, currentIso2) {
    if (!currentIso2 || !window.intlTelInput || typeof window.intlTelInput.getAllCountries !== 'function') {
        return null;
    }
    var countries = window.intlTelInput.getAllCountries();
    var currentCountry = null;
    for (var i = 0; i < countries.length; i++) {
        if (countries[i].iso2 === currentIso2) {
            currentCountry = countries[i];
            break;
        }
    }
    if (!currentCountry) {
        return null;
    }
    var digits = String(text).replace(/\D/g, '');
    if (digits.indexOf(currentCountry.dialCode) !== 0) {
        return null;
    }
    var afterDialCode = digits.slice(currentCountry.dialCode.length);
    var best = null;
    var bestAreaCodeLength = 0;
    for (var j = 0; j < countries.length; j++) {
        var candidate = countries[j];
        if (candidate.dialCode !== currentCountry.dialCode || !candidate.areaCodes) {
            continue;
        }
        for (var k = 0; k < candidate.areaCodes.length; k++) {
            var areaCode = candidate.areaCodes[k];
            if (afterDialCode.indexOf(areaCode) === 0 && areaCode.length > bestAreaCodeLength) {
                best = candidate;
                bestAreaCodeLength = areaCode.length;
            }
        }
    }
    return best;
}

//QA round 6 #2 (admin Manage/Clients) and a follow-up report on the public client-profile
//modal: when this widget's mobile fullscreen country-search popup opens while the field is
//inside a Bootstrap modal, the popup is appended to document.body — intl-tel-input's own
//design, outside the modal's DOM subtree regardless of any dropdownParent option (fullscreen
//mode always ignores it). Two independent Bootstrap modal behaviors then misfire against it:
//(1) the modal's focus-trap (a single document 'focusin.bs.modal' handler — BS3
//Modal.prototype.enforceFocus / BS4 _enforceFocus) sees the search input's focus as "left the
//modal" and yanks focus back, so the search box never actually keeps focus (no keyboard,
//typing does nothing); (2) the modal's Escape-to-close handler is bound on the MODAL element
//itself and — critically — listens for a DIFFERENT event than this library's own Escape
//handling: intl-tel-input only reacts to 'keydown' (#bindCountrySelectorKeydownListener in the
//vendored library), while Bootstrap 3 (htdocs/common/js/bootstrap/bootstrap.js, used on the
//public site) listens on 'keyup' ('keyup.dismiss.bs.modal') and Bootstrap 4 (used in admin) on
//'keydown' ('keydown.dismiss.bs.modal'). Against BS3 specifically, stopping propagation of the
//keydown this library reacts to can never stop the separate keyup Bootstrap is listening for,
//so Escape closes the whole modal instead of just the country popup — which, since it never
//received a matching event either (see (1)), is left visually still open.
//
//Rather than chase either behavior per Bootstrap version, suspend whichever modal is
//currently open for as long as the popup is open, then restore it exactly the way Bootstrap
//itself would (re-running its own re-bind methods). Version-agnostic (tries both BS3's and
//BS4's method/event names) and a no-op when there's no active Bootstrap modal on the page —
//safe to call unconditionally from every consumer, modal or not.
//
//CY1SDEV1-4286: the booking-creation flow opens a ClientForm modal on top of an already-open
//DashboardBookingForm modal (two stacked Bootstrap modals). `.first()` picked whichever modal
//came first in DOM order — the outer booking-form modal, not the inner one the phone field
//actually lives in — so this suspended/restored the wrong modal instance and left the real
//(topmost) modal's focus-trap broken, freezing its other fields after the country popup closed.
//Resolve the modal from the phone field itself (its nearest `.modal` ancestor) so the right
//instance is always picked, regardless of how many modals are stacked; fall back to the old
//"first visible modal" lookup only when the field isn't inside a modal at all (defensive —
//shouldn't happen given the .modal.in/.modal.show guard, but keeps this a safe no-op elsewhere).
function suspendActiveBootstrapModalHandlers(inputEl) {
    if (typeof jQuery === 'undefined') {
        return null;
    }
    var $modal = inputEl ? jQuery(inputEl).closest('.modal.in, .modal.show') : jQuery();
    if (!$modal.length) {
        $modal = jQuery('.modal.in, .modal.show').first();
    }
    if (!$modal.length) {
        return null;
    }
    var modalInstance = $modal.data('bs.modal');
    if (!modalInstance) {
        return null;
    }
    try {
        jQuery(document).off('focusin.bs.modal');
        $modal.off('keyup.dismiss.bs.modal keydown.dismiss.bs.modal');
    } catch (err) {
        //ignore — worst case Bootstrap's handlers still fight the popup
    }
    return modalInstance;
}

function restoreBootstrapModalHandlers(modalInstance) {
    if (!modalInstance) {
        return;
    }
    try {
        if (typeof modalInstance._enforceFocus === 'function') {
            modalInstance._enforceFocus(); //BS4
        } else if (typeof modalInstance.enforceFocus === 'function') {
            modalInstance.enforceFocus(); //BS3
        }
        if (typeof modalInstance._setEscapeEvent === 'function') {
            modalInstance._setEscapeEvent(); //BS4
        } else if (typeof modalInstance.escape === 'function') {
            modalInstance.escape(); //BS3
        }
    } catch (err) {
        //ignore — worst case the modal's own handlers stay suspended until it's next shown
    }
}

//the country search input inside the dropdown shouldn't react to focusin the way this codebase's
//global focus handling expects elsewhere — bound once (not once per IntlInitializer instance).
//QA round 6 #2: on the Manage/Clients mobile form specifically, tapping the search box never
//actually kept focus (no keyboard, typing did nothing), even though this guard existed. Traced
//to Bootstrap's modal focus-trap (Modal.prototype._enforceFocus, bound via jQuery as a single
//document 'focusin.bs.modal' handler): it saw the search input — appended to document.body in
//fullscreen mode, outside the modal's own DOM subtree — as "focus left the modal" and forcibly
//refocused the modal root right after. Tried making this guard capture-phase first, on the
//theory that running before Bootstrap's handler would let stopPropagation() suppress it — that
//didn't hold up in testing (confirmed live), since _enforceFocus reacts by imperatively calling
//.focus() on its own element, which isn't something stopping propagation on this event can
//undo. The actual fix lives in client.form.js instead: it suspends Bootstrap's own
//'focusin.bs.modal' handler for as long as the country selector is open (via the widget's
//'open:countryselector'/'close:countryselector' events) and restores it after, rather than
//racing it. This guard stays plain bubble-phase — it's unrelated to that fix now.
var searchInputFocusinGuardBound = false;
function ensureSearchInputFocusinGuard() {
    if (searchInputFocusinGuardBound) {
        return;
    }
    searchInputFocusinGuardBound = true;
    document.addEventListener('focusin', function (e) {
        if (e.target.closest && e.target.closest('.iti__search-input')) {
            e.preventDefault();
            e.stopPropagation();
        }
    });
}

//module-level (not per-instance) queue for intlTelInput.min.js loading. Client Login can
//initialize two phone widgets (header widget + step form) in the same tick; without this guard
//each one saw `!window.intlTelInput` and injected its own <script>, and the library's own
//top-level `var intlTelInput = ...` has no idempotency check, so the second execution silently
//replaced window.intlTelInput with a brand-new module object (fresh, empty `utils`/`instances`).
//The first widget's `iti.promise` was already fulfilled against the OLD module, but every retry
//guard below reads the CURRENT `window.intlTelInput.utils` — which the replacement zeroed out —
//so its promise-based retry re-subscribed on every keystroke forever (see
//_retryWhenUtilsReady below). null = not requested yet; an array = script is loading, queue of
//callbacks to run once it's ready; non-null non-array (true) = already loaded.
var intlTelInputScriptQueue = null;

function loadScript(url, callback, onError) {
    var script = document.createElement('script');
    script.src = url;
    script.type = 'text/javascript';

    script.onload = function () {
        if (typeof callback === 'function') {
            callback();
        }
    };

    script.onerror = function () {
        console.error('Error loading script: ' + url);
        if (typeof onError === 'function') {
            onError();
        }
    };

    document.head.appendChild(script);
}

//queues onReady behind the single intlTelInput.min.js load rather than letting every concurrent
//IntlInitializer inject its own <script> (see intlTelInputScriptQueue above for why a second,
//redundant script execution silently replaces window.intlTelInput and re-breaks every earlier
//instance's utils-readiness check). Module-level (not a method) so a failed load can re-invoke
//this same request/queue logic per queued callback — each becomes its own fresh leader instead
//of the queue being flushed with a stale, already-failed continuation.
function requestIntlTelInputLibrary(onReady) {
    if (window.intlTelInput) {
        onReady();
        return;
    }
    if (Array.isArray(intlTelInputScriptQueue)) {
        intlTelInputScriptQueue.push(onReady);
        return;
    }
    intlTelInputScriptQueue = [onReady];
    loadScript("/v2/lib/intl-tel-input/js/intlTelInput.min.js?v=4", function () {
        var queued = intlTelInputScriptQueue;
        intlTelInputScriptQueue = true; //truthy, non-array — window.intlTelInput is now set, so the check above takes the "already loaded" branch and nothing re-queues
        for (var i = 0; i < queued.length; i++) {
            //isolate each queued widget — one throwing (e.g. window.intlTelInput() on an
            //element that's since been detached by a re-render) must not stop the rest of the
            //queue from initializing, same as when each had its own independent <script> tag
            try {
                queued[i]();
            } catch (err) {
                console.error('Error initializing queued intl-tel-input widget', err);
            }
        }
    }, function () {
        //load failed (network error, 404, ...) — reset to null and let each queued widget make
        //its own fresh attempt (the first to run becomes the new leader, the rest re-queue onto
        //it) instead of leaving the queue stuck as a permanent array that nothing ever flushes.
        //Without this, one failed request would silently strand every other phone widget on the
        //page for the rest of the session (hidden input never synced, so the field looks normal
        //but saves empty) — worse than the pre-fix behavior, where each widget's own independent
        //failed script only broke that one widget.
        var queued = intlTelInputScriptQueue;
        intlTelInputScriptQueue = null;
        for (var i = 0; i < queued.length; i++) {
            requestIntlTelInputLibrary(queued[i]);
        }
    });
}

var IntlInitializer = function (visibleInput, hiddenInput, options) {
    //own copy per instance — the old jQuery.extend(prototype, {...}) version shared errorMap/options/
    //handler arrays by reference across every IntlInitializer on the page, so e.g. an errorTemplate
    //set by one phone field could silently leak into every other field created afterwards
    this.iti = null;
    this.errorMap = Object.assign({}, DEFAULT_ERROR_MAP);
    this.options = Object.assign({}, DEFAULT_OPTIONS);
    this.inputEl = null;
    this.hiddenInputEl = null;
    this._validHandlers = [];
    this._notValidHandlers = [];

    this.init(visibleInput, hiddenInput, options);
};

IntlInitializer.prototype = Object.create(Object.prototype);

Object.assign(IntlInitializer.prototype, {

    constructor: IntlInitializer,

    init: function (visibleInput, hiddenInput, options) {
        var _this = this;
        if (options) {
            this.options = Object.assign(this.options, options);
        }
        if (this.options.errors) {
            this.setErrorsText(this.options.errors);
        }
        this.inputEl = toElement(visibleInput);

        if (hiddenInput) {
            this.hiddenInputEl = toElement(hiddenInput);
            this.options.hiddenInputs = null;/*function(telInputName) {
                return {
                    phone: _this.hiddenInputEl.dataset.id,
                    //country: "country_code"
                };
            };*/
        }

        if (this.options.onValid) {
            this.onValid(this.options.onValid);
        }
        if (this.options.onNotValid) {
            this.onNotValid(this.options.onNotValid);
        }

        this.initPhoneInput();

        if (window.intlTelInput && window.intlTelInput.utils) {
            window.intlTelInputUtils = window.intlTelInput.utils;
        }

        ensureSearchInputFocusinGuard();
    },

    _getInitialCountyByPrefix: function (prefix) {
        var initialCountry = null;
        if (prefix && window.intlTelInput) {
            var countryData = window.intlTelInput.getAllCountries();
            prefix = String(prefix).replace(/^\D+/g, '');

            for (var i = 0; i < countryData.length; i++) {
                var ccountry = countryData[i];

                if (ccountry && ccountry.dialCode === prefix) {
                    initialCountry = ccountry.iso2;
                }
            }
        }
        return this._rewriteCountryPrefix(initialCountry);
    },

    _getAllowedCountriesByPrefix: function (prefix) {
        var countries = [];
        if (prefix && window.intlTelInput) {
            var countryData = window.intlTelInput.getAllCountries();
            prefix = String(prefix).replace(/^\D+/g, '');

            for (var i = 0; i < countryData.length; i++) {
                var ccountry = countryData[i];

                if (ccountry && ccountry.dialCode === prefix) {
                    countries.push(ccountry.iso2);
                }
            }
        }

        if (!countries.length && prefix) {
            prefix = prefix.substring(0, prefix.length - 1);
            countries = this._getAllowedCountriesByPrefix(prefix);
        }

        return countries;
    },

    initPhoneInput: function () {
        var _this = this;

        var onScriptReady = function () {
            var opts = Object.assign({}, _this.options);
            var initialCountry = null;

            if (opts.countryPrefixId) {
                opts.countryPrefixId = _this._rewriteCountryPrefix(opts.countryPrefixId);
            }

            if (opts.countryPrefixId) {
                opts.initialCountry = String(opts.countryPrefixId).toLowerCase();
            } else if (opts.countryPrefix) {
                initialCountry = _this._getInitialCountyByPrefix(opts.countryPrefix);
                if (initialCountry) {
                    opts.initialCountry = initialCountry;
                }
            }

            if (opts.allowedPrefix) {
                opts.onlyCountries = _this._getAllowedCountriesByPrefix(opts.countryPrefix);

                if (opts.countryPrefixId) {
                    opts.onlyCountries = [String(opts.countryPrefixId).toLowerCase()];
                }
            }

            if (!opts.initialCountry) {
                initialCountry = null;
                var companyConfig = _this.getParam('company_info', {});
                var initialCCountry = _this.getParam('country_phone_initial', null);
                var country = _this.getParam('country', null);

                if (initialCCountry) {
                    initialCountry = String(initialCCountry).toLowerCase();
                } else {
                    if (country) {
                        initialCountry = String(country).toLowerCase();
                    }

                    if (companyConfig && companyConfig.country_id) {
                        initialCountry = String(companyConfig.country_id).toLowerCase();
                    }
                }

                opts.initialCountry = initialCountry;
            }

            if (opts.onlyCountries && opts.initialCountry) {
                if (opts.onlyCountries.indexOf(opts.initialCountry) === -1) {
                    opts.initialCountry = opts.onlyCountries[0];
                }
            }

            if (opts.initialCountry) {
                opts.initialCountry = _this._rewriteCountryPrefix(opts.initialCountry);
            }

            // loadUtils must be a function returning import(utils.js) so intl-tel-input can lazily
            // pull utils.js. A literal import() can't sit in bundled source (r.js/Assetic parsers
            // reject the token), so window.loadUtilsFunction is defined out of band:
            //   - Public booking site: the static /v2/lib/intl-tel-input/js/load-utils.js <script>
            //     in the layout defines it before this bundle runs, so the eval below is skipped —
            //     keeping the public CSP free of 'unsafe-eval' (CY1SDEV1-3838).
            //   - First-party pages (admin, userv2, registration): no such script, so we define it
            //     here via eval(atob(...)) — hiding import() from the bundler. Their CSP
            //     (ContentPolicyPlugin) allows 'unsafe-eval', and this runs synchronously so
            //     opts.loadUtils is set immediately (unlike an async injected <script>).
            // (utils.js?v=4 -- kept in step with product_2's own bump of this same query param;
            // utils.js itself changed content, confirmed identical otherwise via decoded diff.)
            if(!opts.loadUtils){
                if(!window.loadUtilsFunction) {
                    eval(atob("d2luZG93LmxvYWRVdGlsc0Z1bmN0aW9uID0gZnVuY3Rpb24gKCkgewogICAgcmV0dXJuIGltcG9ydCgiL3YyL2xpYi9pbnRsLXRlbC1pbnB1dC9qcy91dGlscy5qcz92PTQiKTsKfQ=="));
                }
                opts.loadUtils = window.loadUtilsFunction;
            }

            //strip wrapper-only bookkeeping options (showError, required, countryPrefix, etc.) —
            //only keys the library actually supports should reach window.intlTelInput()
            for (var optKey in opts) {
                if (opts.hasOwnProperty(optKey) && INTL_TEL_INPUT_SUPPORTED_OPTIONS.indexOf(optKey) === -1) {
                    delete opts[optKey];
                }
            }

            _this.iti = window.intlTelInput(_this.inputEl, opts);

            if (window.intlTelInput && window.intlTelInput.utils) {
                window.intlTelInputUtils = window.intlTelInput.utils;
            }

            //strictMode (library default: on) has a verified upstream bug — reproduced in a
            //jsdom harness against this exact vendored file: pasting a number for a country
            //DIFFERENT from whatever was previously selected computes its max-length trim using
            //the STALE previously-selected country (re-detection from the pasted digits only
            //happens afterward), silently chopping a real digit off the end if the stale
            //country's max length is shorter. E.g. clearing an existing Ukrainian number then
            //pasting a UK number (+447911123456) truncates it to +44791112345 and marks it
            //invalid.
            //
            //Correcting this via a 'paste' listener (tried first) backfires: at 'paste' time the
            //browser hasn't inserted the pasted text into the field yet, so calling
            //setSelectedCountry() there — which itself rewrites the input's value from whatever
            //is CURRENTLY in it — rewrites the OLD (pre-paste) number under the new country,
            //which the browser's paste then inserts into/over incorrectly (reproduced: field
            //ends up with the old number plus a stray extra "+"). Instead, listen for the
            //resulting 'input' event, filtered to paste-originated ones (inputType
            //'insertFromPaste') — by then the browser has already inserted the pasted text, so
            //reading the field's own current value (rather than clipboard data) and correcting
            //the country is safe. Bound on `document` with capture so it runs before the
            //library's own listener on the input element itself — same-target listeners run in
            //registration order regardless of capture, so a capture listener on an ancestor is
            //the only way to reliably run first.
            document.addEventListener('input', function (e) {
                if (e.target !== _this.inputEl || e.inputType !== 'insertFromPaste') {
                    return;
                }
                var currentCountry = _this.iti ? _this.iti.getSelectedCountry() : null;
                var country = findCountryByDialCodePrefix(_this.inputEl.value)
                    || findMostSpecificCountryInFamily(_this.inputEl.value, currentCountry ? currentCountry.iso2 : null);
                if (country && (!currentCountry || currentCountry.iso2 !== country.iso2)) {
                    try {
                        _this.iti.setSelectedCountry(country.iso2);
                    } catch (err) {
                        //ignore — worst case the original upstream trim bug still applies
                    }
                }
            }, true);

            if (_this.iti && _this.iti.promise) {
                _this.iti.promise.then(function () {
                    _this.inputEl.dispatchEvent(new CustomEvent('iti-ready', {bubbles: true}));
                    _this._syncHiddenInput();
                    //only flag an error immediately on load if there's already something in the
                    //field to be wrong about (e.g. editing an existing record with a bad saved
                    //number) — a fresh, untouched, empty required field showing "invalid" before
                    //the user has typed anything is exactly the premature-error complaint this
                    //startCheck split exists to avoid.
                    if (_this.getParam('startCheck', true) && String(_this.inputEl.value).trim()) {
                        _this._updateErrorDisplay();
                    }
                }, function () {
                    //utils.js failed to load (network error, or loadUtils dropped by the library's
                    //own validateOptions) — without this handler the rejection was unobserved.
                    //Nothing to retry here: _retryWhenUtilsReady (see below) already stops after
                    //one attempt once it sees the promise settle. Kept as a visible warning
                    //(rather than fully silent) since this is the only signal that the phone
                    //field will keep accepting input but never sync to the hidden field.
                    console.warn('intl-tel-input: utils.js failed to load — phone validation/hidden-input sync will not work for this field');
                });
            }

            var changeAlwaysEvents = ['change', 'countrychange', 'keyup'];
            var hiddenSyncEvents = ['change', 'countrychange'];
            if (_this.getParam('triggerChangeHidden')) {
                //'input' covers every way the value can change (typing, paste via keyboard OR
                //mouse/context-menu, drag-drop, IME) — 'keyup' alone misses non-keyboard pastes
                //entirely, which is why the Safari "field looks valid but saves as empty" bug
                //(the hidden input staying stale until blur) wasn't fully fixed by keyup alone.
                hiddenSyncEvents.push('keyup', 'input');
            }
            //deliberately NOT hiddenSyncEvents: showing/hiding the error box on every keystroke
            //flashes "invalid" while the user is still mid-way through typing a number that's
            //perfectly valid once complete. The hidden-input sync above needs to run continuously
            //(so Save always reads live data — see the Safari fix this mirrors), but the visible
            //error only makes sense once the user is done editing or switches country.
            var errorDisplayEvents = ['change', 'countrychange'];

            changeAlwaysEvents.forEach(function (eventName) {
                _this.inputEl.addEventListener(eventName, function () {
                    _this._onPhoneInputChangeAlways();
                });
            });

            hiddenSyncEvents.forEach(function (eventName) {
                _this.inputEl.addEventListener(eventName, function () {
                    _this._syncHiddenInput();
                });
            });

            errorDisplayEvents.forEach(function (eventName) {
                _this.inputEl.addEventListener(eventName, function () {
                    _this._updateErrorDisplay();
                });
            });

            //keep the VISIBLE field's text in sync with what's actually being saved (the
            //hidden field, already normalized above via getNumber()). The live as-you-type
            //formatting a user sees while typing doesn't clean up harmless artifacts like a
            //redundant national-trunk "0" typed right after an already-present "+380" (e.g.
            //"+3800321112223") — getNumber() already strips that for the saved value, but the
            //visible field is otherwise left showing the raw, uncleaned text. Bound only to
            //'change'/'countrychange' (never keyup/input) so it never fights the user
            //mid-keystroke — it only cleans up once they're done editing or the country changes.
            ['change', 'countrychange'].forEach(function (eventName) {
                _this.inputEl.addEventListener(eventName, function () {
                    _this._cleanupVisibleValue();
                });
            });

            //see suspendActiveBootstrapModalHandlers above — applies to every consumer, not
            //just ones known to render inside a modal, since it's a no-op when there isn't one.
            var suspendedBootstrapModal = null;
            _this.inputEl.addEventListener('open:countryselector', function () {
                suspendedBootstrapModal = suspendActiveBootstrapModalHandlers(_this.inputEl);
            });
            _this.inputEl.addEventListener('close:countryselector', function () {
                restoreBootstrapModalHandlers(suspendedBootstrapModal);
                suspendedBootstrapModal = null;
            });
        };

        requestIntlTelInputLibrary(onScriptReady);
    },

    //superseded by the module-level loadScript/requestIntlTelInputLibrary above (which needed
    //to queue/retry independently of any single instance), kept as a public method in case
    //something outside this file still calls instance.getScript(...) directly.
    getScript: function (url, callback, onError) {
        loadScript(url, callback, onError);
    },

    //utils.js loads asynchronously; iti.isValidNumber()/getNumber() throw synchronously (the
    //library's own ensureUtils() guard) if called before that import resolves. The three
    //callers below used to reschedule themselves via `iti.promise.then(retry)` directly — safe
    //ONLY while that promise is still pending. If it's already fulfilled/rejected but
    //window.intlTelInput.utils is still falsy (e.g. a second, concurrent IntlInitializer's
    //<script> load replaced window.intlTelInput with a fresh module object whose utils hasn't
    //loaded yet — see intlTelInputScriptQueue above), `.then()` runs the retry in the very next
    //microtask, which finds utils still missing and re-subscribes again — an unbounded
    //microtask loop with no exception and no console output, starving the macrotask queue so
    //utils can never finish loading either (reproduced live: pins a CPU core at 100%, page
    //"Unresponsive"). Retry at most once per method per instance, and only actually re-run the
    //method if utils genuinely showed up by the time the promise settled.
    //Returns true if the caller should bail out (utils not ready yet), false if it's safe to proceed.
    _retryWhenUtilsReady: function (key, method) {
        if (!window.intlTelInput || window.intlTelInput.utils) {
            return false;
        }
        if (!this._utilsRetryPending) {
            this._utilsRetryPending = {};
        }
        if (this._utilsRetryPending[key] || !this.iti || !this.iti.promise) {
            return true;
        }
        this._utilsRetryPending[key] = true;
        var _this = this;
        var settled = function () {
            _this._utilsRetryPending[key] = false;
            if (window.intlTelInput && window.intlTelInput.utils) {
                method.call(_this);
            }
        };
        this.iti.promise.then(settled, settled);
        return true;
    },

    _onPhoneInputChangeAlways: function () {
        var _this = this;
        if (!this.iti) {
            console.warn('input not initialized');
            return;
        }
        if (this._retryWhenUtilsReady('changeAlways', this._onPhoneInputChangeAlways)) {
            return;
        }
        this._refineCountryWithinFamily();
        var handlers = this.isPhoneValid() ? this._validHandlers : this._notValidHandlers;
        for (var i = 0; i < handlers.length; i++) {
            if (typeof handlers[i] === 'function') {
                var result = handlers[i](_this.getError());
                if (result === false) {
                    break;
                }
            }
        }
    },

    //see findMostSpecificCountryInFamily above — called on every 'change'/'countrychange'/
    //'keyup' (typing, blur, and as a fallback for paste methods that don't fire 'keyup') so a
    //bare-digit number gets the same area-code-specific country resolution a "+"-prefixed one
    //already gets from the library itself. setSelectedCountry() re-dispatches 'countrychange',
    //which re-enters this method — safe: the second pass finds the now-current country is
    //already the most specific match, so it's a no-op instead of a loop.
    _refineCountryWithinFamily: function () {
        if (!this.iti) {
            return;
        }
        var currentCountry = this.iti.getSelectedCountry();
        if (!currentCountry) {
            return;
        }
        var country = findMostSpecificCountryInFamily(this.inputEl.value, currentCountry.iso2);
        if (country && country.iso2 !== currentCountry.iso2) {
            try {
                this.iti.setSelectedCountry(country.iso2);
            } catch (err) {
                //ignore — worst case the pre-existing family ambiguity remains
            }
        }
    },

    //re-runs the visible field's value through the library's full setNumber()/getNumber()
    //round trip so it displays the same cleaned-up number already being saved (see the
    //'change'/'countrychange' listener above for why). setNumber() re-dispatches a native
    //'input' event, which can loop back into this same 'change'/'countrychange' handler chain —
    //safe: the second pass reformats the now-already-clean value into itself, settling
    //immediately instead of looping (verified empirically, not just assumed).
    _cleanupVisibleValue: function () {
        if (!this.iti || typeof this.iti.setNumber !== 'function' || typeof this.iti.getNumber !== 'function') {
            return;
        }
        var normalized = this.iti.getNumber();
        if (!normalized) {
            return;
        }
        try {
            this.iti.setNumber(normalized);
        } catch (err) {
            //ignore — worst case the visible field keeps showing the raw, unreformatted text
        }
    },

    //keeps the hidden input (what Save actually reads) in sync with the live number — split out
    //from error display below so this can run on every keystroke without also flashing an
    //"invalid" error mid-typing (see the hiddenSyncEvents/errorDisplayEvents split in
    //initPhoneInput).
    _syncHiddenInput: function () {
        if (!this.iti) {
            console.warn('input not initialized');
            return;
        }
        //see _retryWhenUtilsReady above — same utils.js readiness race; rescheduling here
        //is what keeps the hidden input from staying stale.
        if (this._retryWhenUtilsReady('sync', this._syncHiddenInput)) {
            return;
        }
        var number = this.iti.getNumber();

        if (this.hiddenInputEl) {
            this.hiddenInputEl.value = number;
            this.hiddenInputEl.dispatchEvent(new Event('change', {bubbles: true}));
        }
    },

    //shows/hides the error box + has-error border. Bound only to 'change'/'countrychange' (blur,
    //country switch) — never keyup/input — so a number that's merely incomplete mid-typing
    //doesn't flash "invalid" before the user's finished entering it.
    _updateErrorDisplay: function () {
        if (!this.iti) {
            console.warn('input not initialized');
            return;
        }
        //see _retryWhenUtilsReady above — same utils.js readiness race.
        if (this._retryWhenUtilsReady('errorDisplay', this._updateErrorDisplay)) {
            return;
        }
        var showError = this.getParam('showError', true);
        var showErrorBorder = this.getParam('showErrorBorder', true);

        var formGroup = this.inputEl.closest('.form-group');
        if (formGroup) {
            if (showErrorBorder) {
                formGroup.classList.remove('has-error');
            }
            if (showError) {
                formGroup.querySelectorAll('.invalid-feedback, .input-error').forEach(function (el) {
                    el.remove();
                });
            }
        }

        if (!this.isPhoneValid() && formGroup) {
            if (showError) {
                var errorMsg = this.getError();
                var template = this.getParam('errorTemplate')
                    ? String(this.getParam('errorTemplate')).replace('{{error}}', Locale.get(errorMsg))
                    : '<p class="input-error">' + Locale.get(errorMsg) + '</p>';
                var output = htmlToElement(template);
                output.classList.add('invalid-feedback');

                var itiEl = formGroup.querySelector('.iti');
                if (itiEl) {
                    itiEl.insertAdjacentElement('afterend', output);
                }
            }
            if (showErrorBorder) {
                formGroup.classList.add('has-error');
            }
        }
    },

    getInstance: function () {
        return this.iti;
    },

    isPhoneValid: function () {
        if (this.iti) {
            //utils.js loads asynchronously; getNumber()/isValidNumber() throw synchronously
            //(the library's own ensureUtils() guard) if called before that import resolves.
            //Unlike the 'change'/'input' handlers above, this is a PUBLIC method some consumers
            //(e.g. client.form.js's _isFormValid, called directly at save time) call with no
            //readiness guard of their own — reproduced in Safari specifically, where utils.js's
            //dynamic import() is slower: pasting a number and clicking Save quickly enough hits
            //this window, and the field gets reported as invalid/required even though it's a
            //correctly typed, valid number. Treat "can't tell yet" as valid rather than block
            //submission on a false rejection.
            if (window.intlTelInput && !window.intlTelInput.utils) {
                return true;
            }
            var required = this.getParam('required', true);
            var number = this.iti.getNumber();
            var isValid = this.getParam('strictValidation', false)
                ? this.iti.isValidNumberPrecise()
                : this.iti.isValidNumber();
            var isNotValid = !isValid && (required || (!required && number));
            return !isNotValid;
        }
        return false;
    },

    getError: function () {
        if (!this.isPhoneValid() && this.iti) {
            var errorCode = this.iti.getValidationError();

            var errorMsg = this.errorMap[errorCode];
            if (!errorMsg) {
                errorMsg = this.errorMap[0];
            }
            return errorMsg;
        }
        return '';
    },

    getParam: function (paramKey, defaultValue) {
        if (typeof this.options[paramKey] === 'undefined') {
            return defaultValue;
        }
        return this.options[paramKey];
    },

    setErrorsText: function (errors) {
        this.errorMap = Object.assign(this.errorMap, errors);
    },

    setErrorText: function (n, error) {
        if (error) {
            this.errorMap[n] = error;
        }
    },

    setCountry: function (id) {
        if (this.iti) {
            this.iti.setSelectedCountry(id); //renamed from setCountry in v29
        }
    },

    onValid: function (handler) {
        this._validHandlers.push(handler);
    },

    onNotValid: function (handler) {
        this._notValidHandlers.push(handler);
    },

    _rewriteCountryPrefix: function (prefix) {
        if (String(prefix).toUpperCase() === 'AN') { // Netherlands Antilles not exist in Intl lib, must be Curaçao
            return 'CW';
        }
        return prefix;
    }

});

define("google.intl.phone.init", ["jquery"], function(){});

